



























































import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import { DataTableHeader } from 'vuetify';
import { Alert, NoticeClass, AuthStore } from '@kasasa/fbase-components/lib';
import {
  AccountQualificationDetailsResource,
  AccountQualificationDetail,
  AccountQualificationOverride,
} from '@/api/accountqualificationdetails';
import { Cycle } from '@/api/cycles';
import { Account } from '@/api/consumeraccountdetails';
import ForceQualifyControl from '@/components/account/ForceQualifyControl.vue';
import TransactionHistorySheet from '@/components/account/TransactionHistorySheet.vue';

interface QualificationDetailTableEntry {
  [keys: string]: any;
  cycleId: number;
  cycleName: string;
  qualified: string;
  qualifiedLevel: string;
  pos: number | null;

  achCredit: number | null,
  achCreditOrBillPay: number | null,
  achDebit: number | null,
  achDebitOrCredit: number | null,
  achDebitCreditOrBillPay: number | null,
  achDebitOrBillPay: number | null,

  ccOrPos: number | null;
  ccCount: number | null;
  ccSpend: number | null;
  ccCountAndSpend: number | null;
  ccCountOrSpend: number | null;

  loan: number | null;

  estatement: boolean | null;
  ibLogin: boolean | null;
  ibLoginDate: string | null;
  ibLoginSortDate: number | null;
  autoQual: string;
  sortDate: number;
}

const SIXTY_FIVE_DAYS: number = 1000 * 60 * 60 * 24 * 65;

@Component({
  computed: mapState(['fiId']),
  components: {
    ForceQualifyControl,
    TransactionHistorySheet,
  },
})
export default class AccountQualificationDetailTable extends Vue {
  @Prop() account!: Account;

  @Prop() cycles!: Cycle[];

  @Prop() relationship!: string;

  qualificationItems: QualificationDetailTableEntry[] = [];

  qualificationDetails: AccountQualificationDetail[] = [];

  fiId!: string;

  loading: boolean = true;

  loadingMore: boolean = false;

  nextCycleIndex: number = 0;

  headers: DataTableHeader[] = [];

  @Inject('AccountQualificationDetailsResource')
  qualDetailsResource!: AccountQualificationDetailsResource;

  showTransactionHistorySheet: boolean = false;

  transactionHistoryCycleId: string | null = null;

  transactionHistoryTypes: string[] = [];

  transactionHistoryExpectedEntries: number = 0;

  static readonly columnValueTransactionHistoryTypeMap = new Map<string, string[]>([
    ['pos', ['POS']],
    ['achCredit', ['ACH_CREDIT']],
    ['achCreditOrBillPay', ['ACH_CREDIT', 'BILL_PAY']],
    ['achDebit', ['ACH_DEBIT']],
    ['achDebitOrCredit', ['ACH_DEBIT', 'ACH_CREDIT']],
    ['achDebitCreditOrBillPay', ['ACH_DEBIT', 'ACH_CREDIT', 'BILL_PAY']],
    ['achDebitOrBillPay', ['ACH_DEBIT', 'BILL_PAY']],
  ]);

  mounted() {
    this.loadDetails();
  }

  @Watch('account')
  async loadDetails() {
    this.loading = true;
    this.qualificationItems = [];
    this.qualificationDetails = [];
    if (this.fiId && this.account && this.cycles.length > 0) {
      this.nextCycleIndex = this.cycles.length - 1;
      this.qualificationItems.push(
        await this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]),
      );

      this.buildHeaders(this.qualificationDetails[0]);

      if (this.nextCycleIndex > -1) {
        this.qualificationItems.push(
          await this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]),
        );
        this.qualificationItems.sort(this.compareQualificationCycles);

        this.qualificationItems[1].cycleName = `Previous (${this.qualificationItems[1].cycleName})`;
      }
      this.qualificationItems.sort(this.compareQualificationCycles);
      this.qualificationItems[0].cycleName = `Current (${this.qualificationItems[0].cycleName})`;
    }
    this.loading = false;
  }

  // The order of this sort is reversed so the result is in descending order
  compareQualificationCycles = (qualCycle1: QualificationDetailTableEntry, qualCycle2: QualificationDetailTableEntry): number => {
    if (qualCycle1.sortDate > qualCycle2.sortDate) {
      return -1;
    } if (qualCycle1.sortDate < qualCycle2.sortDate) {
      return 1;
    }
    return 0;
  }

  async loadMore() {
    if (
      this.nextCycleIndex > -1
      && this.qualificationItems
      && this.qualificationItems.length > 1
      && !this.loadingMore
    ) {
      this.loadingMore = true;
      const promises: Promise<QualificationDetailTableEntry>[] = [];
      for (let i = 0; i < 10 && this.nextCycleIndex > -1; i++) {
        promises.push(
          this.loadDetailsForCycle(this.cycles[this.nextCycleIndex--]),
        );
      }
      // Make sure results are sorted by cycle id before adding to table
      await Promise.all(promises).then(
        (values: QualificationDetailTableEntry[]) => {
          this.qualificationItems = this.qualificationItems.concat(values);
          this.qualificationItems.sort(this.compareQualificationCycles);
        },
      );
      this.loadingMore = false;
    }
  }

  async loadDetailsForCycle(
    cycle: Cycle,
  ): Promise<QualificationDetailTableEntry> {
    try {
      const detail: AccountQualificationDetail = await this.qualDetailsResource.getAccountQualificationDetail(
        this.fiId,
        cycle.id,
        this.account.productId,
        this.account.accountKasasaKey,
      );
      this.qualificationDetails.push(detail);
      return AccountQualificationDetailTable.buildTableEntry(detail, cycle);
    } catch (error) {
      this.showErrorMessage(error);
      throw error;
    }
  }

  buildHeaders(detail: AccountQualificationDetail) {
    this.headers.push({ text: 'Qualification Cycle', value: 'sortDate' });
    this.headers.push({ text: 'Qualified', value: 'qualified' });
    this.headers.push({ text: 'Qualified Level', value: 'qualifiedLevel' });

    if (detail.pos != null) {
      this.headers.push({ text: 'POS', value: 'pos' });
    }

    this.buildACHHeaders(detail);
    this.buildCCHeaders(detail);

    if (detail.loan != null) {
      this.headers.push({ text: 'Loan', value: 'loan' });
    }

    if (detail.estatement != null) {
      this.headers.push({ text: 'E-Statement', value: 'estatement' });
    }

    if (detail.iblogin != null) {
      this.headers.push({ text: 'IBLogin', value: 'ibLogin' });
      this.headers.push({ text: 'IBLogin Date', value: 'ibLoginSortDate' });
    }

    this.headers.push({ text: 'AutoQual', value: 'autoQual' });
    this.headers.push({ text: 'Force Qualify?', value: '', sortable: false });
  }

  buildACHHeaders(detail: AccountQualificationDetail) {
    if (detail.achCredit != null) {
      this.headers.push({ text: 'ACH Credit', value: 'achCredit' });
    }

    if (detail.achCreditOrBillPay != null) {
      this.headers.push({ text: 'ACH Credit or Bill Pay', value: 'achCreditOrBillPay' });
    }

    if (detail.achDebit != null) {
      this.headers.push({ text: 'ACH Debit', value: 'achDebit' });
    }

    if (detail.achDebitOrCredit != null) {
      this.headers.push({ text: 'ACH Debit or Credit', value: 'achDebitOrCredit' });
    }

    if (detail.achDebitCreditOrBillPay != null) {
      this.headers.push({ text: 'ACH Debit, Credit or Bill Pay', value: 'achDebitCreditOrBillPay' });
    }

    if (detail.achDebitOrBillPay != null) {
      this.headers.push({ text: 'ACH Debit or Bill Pay', value: 'achDebitOrBillPay' });
    }
  }

  buildCCHeaders(detail: AccountQualificationDetail) {
    if (detail.ccOrPos != null) {
      this.headers.push({ text: 'CC or POS', value: 'ccOrPos' });
    }

    if (detail.ccCount != null) {
      this.headers.push({ text: 'CC Count', value: 'ccCount' });
    }

    if (detail.ccSpend != null) {
      this.headers.push({ text: 'CC Spend', value: 'ccSpend' });
    }

    if (detail.ccCountAndSpend != null) {
      this.headers.push({
        text: 'CC Count and Spend',
        value: 'ccCountAndSpend',
      });
    }

    if (detail.ccCountOrSpend != null) {
      this.headers.push({ text: 'CC Count or Spend', value: 'ccCountOrSpend' });
    }
  }

  static buildTableEntry(
    detail: AccountQualificationDetail,
    cycle: Cycle,
  ): QualificationDetailTableEntry {
    return {
      cycleId: cycle.id,
      cycleName:
        `${new Date(cycle.qualificationStartDate).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        })} `
        + `- ${new Date(cycle.qualificationEndDate).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        })}`,
      qualified: detail.qualified ? 'Yes' : 'No',
      qualifiedLevel: detail.highestQualifyingLevel
        ? detail.highestQualifyingLevel
        : '0',
      pos: detail.pos,

      achCredit: detail.achCredit,
      achCreditOrBillPay: detail.achCreditOrBillPay,
      achDebit: detail.achDebit,
      achDebitOrCredit: detail.achDebitOrCredit,
      achDebitCreditOrBillPay: detail.achDebitCreditOrBillPay,
      achDebitOrBillPay: detail.achDebitOrBillPay,

      ccOrPos: detail.ccOrPos,
      ccCount: detail.ccCount,
      ccSpend: detail.ccSpend,
      ccCountAndSpend: detail.ccCountAndSpend,
      ccCountOrSpend: detail.ccCountOrSpend,

      loan: detail.loan,
      estatement: detail.estatement,
      ibLogin: detail.iblogin,
      ibLoginDate: detail.ibloginDate
        ? new Date(detail.ibloginDate).toLocaleDateString('en-US', {
          timeZone: 'UTC',
        })
        : '-',
      ibLoginSortDate: detail.ibloginDate
        ? new Date(detail.ibloginDate).getTime()
        : null,
      autoQual: detail.autoQualified ? 'Yes' : 'No',
      sortDate: new Date(cycle.qualificationEndDate).getTime(),
      override: detail.accountQualificationOverride,
      cycle,
    };
  }

  getTableValue = (
    item: QualificationDetailTableEntry,
    header: any,
  ): String => {
    let value: String = '';

    if (header.value) {
      if (header.value === 'sortDate') {
        value = item.cycleName;
      } else if (header.value === 'qualified') {
        value = item.qualified;
      } else if (header.value === 'qualifiedLevel') {
        value = item.qualifiedLevel;
      } else if (header.value === 'pos') {
        value = item.pos ? item.pos.toString() : '0';
      } else if (header.value === 'pos' || header.value.startsWith('ach')
        || header.value.startsWith('cc')) {
        value = item[header.value] ? item[header.value].toString() : '0';
      } else if (header.value === 'loan' && item.loan !== null) {
        value = item.loan.toString();
      } else if (header.value === 'estatement' && item.estatement !== null) {
        value = item.estatement ? 'Yes' : 'No';
      } else if (header.value === 'ibLogin' && item.ibLogin !== null) {
        value = item.ibLogin ? 'Yes' : 'No';
      } else if (header.value === 'ibLoginSortDate') {
        value = item.ibLoginDate ? item.ibLoginDate : '-';
      } else if (header.value === 'autoQual') {
        value = item.autoQual;
      }
    }
    return value;
  };

  tableValueShouldLinkToTransactionHistorySheet = (
    item: QualificationDetailTableEntry,
    columnValue: string,
  ): boolean => {
    let shouldLinkToTransactions = false;

    if (
      (columnValue === 'pos' && item.pos && item.pos > 0)
      || (columnValue.startsWith('ach') && item[columnValue] && item[columnValue] > 0)
    ) {
      // Cycle must have started < 66 days prior
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      shouldLinkToTransactions = today.getTime()
        - new Date(item.cycle.qualificationStartDate).getTime()
        <= SIXTY_FIVE_DAYS;
    }
    return shouldLinkToTransactions;
  };

  showTransactionHistory(
    item: QualificationDetailTableEntry,
    columnValueType: string,
  ) {
    this.transactionHistoryCycleId = item.cycleId.toString();
    const historyType: string[] | undefined = AccountQualificationDetailTable.columnValueTransactionHistoryTypeMap.get(columnValueType);
    this.transactionHistoryTypes = historyType || [];
    this.transactionHistoryExpectedEntries = item[columnValueType];
    this.showTransactionHistorySheet = true;
  }

  showErrorMessage(error: string) {
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
